import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { Select, Typography } from "antd";
import styles from "./ConversationHeader.module.less";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CopilotMessageContext } from "~/providers/CopilotMessageProvider";
import { useLanguage } from "~/hooks/useLanguage";
import moment from "moment";
import { useGetCampaignHeatsForAsset } from "~/pages/copilotMessage/hooks/useGetCampaignHeatsForAsset";
import { useUserLogContext } from "~/providers/UserLogProvider";
import { CopilotLogsType } from "~/pages/copilotMessage/types/copilotLogs";
import { getRequestsForHeat } from "~/pages/copilotMessage/services/copilot";
import {
  getCampaignHeatId,
  isConversationGeneric,
} from "~/pages/copilotMessage/components/conversationDetail/components/conversationMessages/components/mikeMessage/helpers/mikeMessageParametersHelper";
import { mapRequestToRequestResponseViewModel } from "~/pages/copilotMessage/helpers/copilotMessageMapper";
import { ResponseViewModel } from "~/pages/copilotMessage/types/copilot";

function ConversationHeader() {
  const scrollByMikeIdRef = useRef<() => void>();
  const [currentCampaignHeatId, setCurrentCampaignHeatId] =
    useState<string>("");

  const {
    isConversationListExpanded,
    setIsConversationListExpanded,
    selectedConversation,
    selectedConversationRef,
    requestLoading,
    setConversationResponse,
    conversationResponse,
    responses,
  } = useContext(CopilotMessageContext);

  const { insertUserLog } = useUserLogContext();

  // Check if this is a generic conversation
  const isGeneric = isConversationGeneric(selectedConversation?.id);

  const setCampaignHeatId = useCallback(
    (campaignHeatId: string | undefined) => {
      setCurrentCampaignHeatId(campaignHeatId ?? "");
    },
    []
  );

  const { data: heatList, loading: heatListLoading } =
    useGetCampaignHeatsForAsset(selectedConversation?.assetId ?? "");

  const heatListForSelect = useMemo(
    () =>
      heatList?.map((x) => ({
        label: x.displayText,
        value: x.campaignHeatId,
      })),
    [heatList]
  );

  // Select dropdown'dan heat seçildiğinde çağrılan fonksiyon
  const onChangeSelectedHeat = useCallback(
    async (campaignHeatId: string) => {
      if (selectedConversation?.id && campaignHeatId) {
        try {
          let lastResponseId: string | undefined;
          let relatedResponses: ResponseViewModel[] = [];

          const heatRequests = await getRequestsForHeat(campaignHeatId);
          const mappedData = mapRequestToRequestResponseViewModel(
            heatRequests,
            true
          );

          if (mappedData?.responses && mappedData.responses.length > 0) {
            const sortedResponses = mappedData.responses.sort((a, b) =>
              moment(a.operationDate).diff(moment(b.operationDate))
            );
            relatedResponses = sortedResponses;
            const notIncludedFromResponses = relatedResponses.filter(
              (r) => !responses.some((x) => x.id === r.id)
            );
            lastResponseId =
              notIncludedFromResponses[notIncludedFromResponses.length - 1]?.id;
          }

          // 5️⃣ SearchedHeat'i set et (responseId varsa scroll tetiklenir)
          setConversationResponse({
            conversationId: selectedConversation.id,
            responseId: lastResponseId,
            responsesMap: lastResponseId
              ? {
                  [lastResponseId]: relatedResponses,
                }
              : undefined,
          });

          setCampaignHeatId(campaignHeatId);
          insertUserLog(CopilotLogsType.Searched);
        } catch (error) {
          setCampaignHeatId(undefined);
        }
      } else {
        setCampaignHeatId(undefined);
      }
    },
    [
      insertUserLog,
      selectedConversation?.id,
      setConversationResponse,
      setCampaignHeatId,
      responses,
    ]
  );

  // Effect to get campaignHeatId from Response service when responseId changes
  useEffect(() => {
    if (requestLoading) return;

    if (conversationResponse?.responseId) {
      const response = responses.find(
        (r) => r.id === conversationResponse.responseId
      );

      if (response) {
        // Use helper function to get campaignHeatId based on actionType
        const campaignHeatId = getCampaignHeatId(
          response.actionType,
          response.parameters
        );
        setCampaignHeatId(campaignHeatId);
      } else {
        setCampaignHeatId(undefined);
      }
    } else {
      setCampaignHeatId(undefined);
    }
  }, [
    conversationResponse?.responseId,
    responses,
    requestLoading,
    setCampaignHeatId,
    selectedConversationRef,
  ]);

  const hasSearchedHeatInList = useMemo(() => {
    return (
      currentCampaignHeatId !== undefined &&
      heatListForSelect?.find((x) => x.value === currentCampaignHeatId)
    );
  }, [heatListForSelect, currentCampaignHeatId]);

  const changeConversationListVisibility = useCallback(() => {
    setIsConversationListExpanded((prev) => !prev);
  }, [setIsConversationListExpanded]);

  const { formatDate, formatMessage } = useLanguage();

  const campaignStartDate = useMemo(() => {
    const dateString =
      (selectedConversation?.currentCampaignName ?? "").split("-")[1] ?? "";
    return dateString ? moment(dateString, "YYYYMMDD").toDate() : new Date();
  }, [selectedConversation]);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      scrollByMikeIdRef.current?.();
    };
  }, []);

  return (
    <div className={styles.ConversationHeader}>
      <div className={styles.leftWrapper}>
        {isConversationListExpanded ? (
          <MenuFoldOutlined onClick={changeConversationListVisibility} />
        ) : (
          <MenuUnfoldOutlined onClick={changeConversationListVisibility} />
        )}

        <Typography.Title level={1} className={styles.ladleNumber}>
          {`Ladle ${
            isGeneric
              ? formatMessage("LadleCard.Generic.Unassigned")
              : (selectedConversation?.assetName ?? "")
          }`}
        </Typography.Title>

        {/* Hide campaign information for generic conversations */}
        {!isGeneric && (
          <>
            <div className={styles.campaignWrapper}>
              <Typography.Title level={3} className={styles.campaignLabel}>
                {formatMessage("ConversationHeader.Campaign")}
              </Typography.Title>

              <Typography.Title level={2} className={styles.campaignId}>
                {selectedConversation?.currentCampaignName}
              </Typography.Title>
            </div>

            <div className={styles.dateWrapper}>
              <Typography.Title level={3} className={styles.dateLabel}>
                {formatMessage("ConversationHeader.StartDate")}
              </Typography.Title>
              <Typography.Title level={2} className={styles.date}>
                {formatDate(campaignStartDate)}
              </Typography.Title>
            </div>
          </>
        )}
      </div>

      {/* Hide heat search for generic conversations */}
      {!isGeneric && (
        <div className={styles.rightWrapper}>
          <Select
            showSearch
            allowClear
            onClear={() => {
              setConversationResponse(undefined);
            }}
            key={selectedConversation?.id}
            loading={heatListLoading || requestLoading}
            className={styles.searchInput}
            placeholder={formatMessage("ConversationHeader.SearchHeat")}
            optionFilterProp="label"
            value={hasSearchedHeatInList ? currentCampaignHeatId : ""}
            onChange={onChangeSelectedHeat}
            options={heatListForSelect}
          />
        </div>
      )}
    </div>
  );
}

export default ConversationHeader;
